import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useDecision } from '@optimizely/react-sdk'
import Loader from '@space-core/components/loader'
import { Actions, Entities } from '@space-core/datahub'
import { useIntersectionObserver } from '@space-core/hooks/useIntersectionObserver'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import { Context } from '@space-shell/common/services/notificationCenter'

import { BaseNames } from '@space-shell/core/router/constants'
import { timeSince } from '@space-shell/modules/notifications/utils/date'

import { useTranslation } from 'react-i18next'

import { NotificationList } from './styles'

import {
  useActivitiesNotificationCount,
  useNotifications,
  useReadAllNotifications
} from '../../hooks'
import LoadingState from '../loadingState'
import NotificationAvatar from '../notificationAvatar'
import NotificationCounter from '../notificationCounter'
import NotificationEmptyState from '../notificationEmptyState'
import NotificationErrorState from '../notificationErrorState'
import NotificationListItem from '../notificationListItem'

const ADDITIONAL_FOR_PENULTIMATE_POSITION = 2

const ActivitiesNotificationsList = () => {
  const [{ variables }] = useDecision('com_eng_web_enable_new_notification_center')
  const isEnabledAutoReadAllNotifications = Boolean(variables?.enable_auto_read_notification)
  const { data: activitiesNotificationCount } = useActivitiesNotificationCount()
  const {
    data: activities,
    isLoading,
    isFetchingNextPage,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isError,
    isSuccess
  } = useNotifications(Context.ACTIVITIES)

  const { mutate: readAll } = useReadAllNotifications(Context.ACTIVITIES)
  const { t, i18n } = useTranslation()
  const { sendDataHubEvent } = useDataHubEvents()

  const language = String(i18n.language).toLowerCase()

  const handleClick = ({
    membershipSlug,
    communityId,
    postId
  }: {
    membershipSlug: string
    communityId: string
    postId: string
  }) => {
    sendDataHubEvent({
      group: Entities.NOTIFICATIONS_CENTER,
      name: Actions.CLICK_NOTIFICATIONS_CENTER_ACTIVITIE_ITEM
    })
    window.location.assign(
      BaseNames.communityPostDetail(membershipSlug, language, communityId, postId)
    )
  }

  const observer = useRef<IntersectionObserver>()

  const penultimateElementRef = useIntersectionObserver({
    observer,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage: Boolean(hasNextPage)
  })

  const isEmptyNotifications =
    isSuccess && (!activities?.pages?.[0] || activities?.pages?.[0]?.notifications?.length === 0)

  const hasUnreadNotifications = useMemo(
    () => Boolean(activitiesNotificationCount?.unread),
    [activitiesNotificationCount?.unread]
  )

  const handleOnMarkAllAsRead = useCallback(() => {
    sendDataHubEvent({
      group: Entities.NOTIFICATIONS_CENTER,
      name: Actions.CLICK_NOTIFICATIONS_CENTER_ACTIVITIE_READ_ALL
    })
    readAll()
  }, [readAll, sendDataHubEvent])

  useEffect(() => {
    return () => {
      if (isEnabledAutoReadAllNotifications && hasUnreadNotifications) {
        handleOnMarkAllAsRead()
      }
    }
  }, [handleOnMarkAllAsRead, hasUnreadNotifications, isEnabledAutoReadAllNotifications])

  if (isLoading) {
    return <LoadingState />
  }

  if (isEmptyNotifications) {
    return <NotificationEmptyState />
  }

  if (isError) {
    return <NotificationErrorState />
  }

  return (
    <>
      <NotificationList>
        {(activities?.pages ?? []).map((page) =>
          (page?.notifications ?? []).map(({ content, read, time, metadata }, index) => {
            const { key: timeLabelKey, value: formattedTime } = timeSince(time)
            const pluralSufix = formattedTime > 1 ? '_plural' : ''

            const formattedDate = t(`general.time_from_now.${timeLabelKey}${pluralSufix}`, {
              value: formattedTime
            })

            metadata.notificationType

            return (
              <NotificationListItem
                key={index}
                ref={
                  page.notifications.length === index + ADDITIONAL_FOR_PENULTIMATE_POSITION &&
                  !isError
                    ? penultimateElementRef
                    : undefined
                }
                isRead={read}
                onClick={() =>
                  handleClick({
                    membershipSlug: metadata.membershipSlug,
                    communityId: metadata.community,
                    postId: metadata.post
                  })
                }
                content={content}
                footer={`${formattedDate} . ${metadata.communityName}`}
                avatar={
                  <NotificationAvatar
                    userCode={metadata.user}
                    userName={metadata.userFirstAndMiddleNames}
                  />
                }
              />
            )
          })
        )}
        {isFetchingNextPage && (
          <div className='_d-flex _justify-content-center'>
            <Loader />
          </div>
        )}
      </NotificationList>
      <NotificationCounter
        totalUnreadNotifications={activitiesNotificationCount?.unread}
        onMarkAsRead={handleOnMarkAllAsRead}
        hideMarkAsReadButton={isEnabledAutoReadAllNotifications}
      />
    </>
  )
}

export default ActivitiesNotificationsList
